import type { Employee } from "@evolua/api-client";
import { EmptyState, PartialState, Select, Skeleton, buttonClassName } from "@evolua/ui";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { flattenCursorPages } from "../api/cursor.js";
import { DEMO_FLAG_KEYS, useFeatureFlag } from "../api/use-feature-flags.js";
import { useEmployeeDirectory } from "../api/use-employees.js";
import { getApiClient } from "../auth/http.js";
import { EmployeeDirectory } from "../components/employees/EmployeeDirectory.js";
import { EmployeeNoteForm } from "../components/forms/EmployeeNoteForm.js";
import { useSession } from "../session/SessionContext.js";

/** Porta de entrada RH: diretório (T26) + notas (flag). */
export function PessoasPage() {
  const { isDemoMode } = useSession();
  const notesEnabled = useFeatureFlag(DEMO_FLAG_KEYS.employeeNotesEnabled);
  const query = useEmployeeDirectory({ sort: "fullName:asc", limit: 50 });
  const rows = flattenCursorPages(query.data?.pages ?? []);
  const [selectedId, setSelectedId] = useState<string>("");

  const selected: Employee | undefined = useMemo(
    () => rows.find((r) => r.id === selectedId),
    [rows, selectedId],
  );

  return (
    <>
      <EmployeeDirectory
        title="Pessoas"
        lead="Porta de entrada do RH — diretório com busca, filtros, origem do dado e exportação auditada"
        caption="Diretório de colaboradores (RH)"
        detailBasePath="/pessoas"
      />

      <section className="evolua-stack evolua-stack--3">
        <h2 className="evolua-section-title">Nota do colaborador</h2>
        <p className="evolua-page-lead">
          Formulário com validação `@evolua/schemas`
          {isDemoMode ? " (demo local)." : "."}
        </p>

        {notesEnabled === false ? (
          <PartialState
            title="Notas desabilitadas"
            description="A flag employee.notes.enabled está desligada para este tenant. A API também rejeita com 403."
            action={
              <Link
                to="/configuracoes/flags"
                className={buttonClassName({ variant: "secondary" })}
              >
                Abrir feature flags
              </Link>
            }
          />
        ) : null}

        <Select
          label="Colaborador"
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          options={[
            { value: "", label: "Selecione…" },
            ...rows.map((r) => ({ value: r.id, label: r.fullName })),
          ]}
        />

        {notesEnabled === undefined ? (
          <div className="evolua-mt">
            <Skeleton height="6rem" label="Carregando flags" />
          </div>
        ) : null}

        {selected && notesEnabled === false ? (
          <EmptyState
            title="Formulário indisponível"
            description="Ligue a flag neste tenant para registrar notas."
          />
        ) : null}

        {selected && notesEnabled ? (
          <div className="evolua-stack evolua-stack--3">
            <EmployeeNoteForm
              employeeLabel={selected.fullName}
              onSubmit={async (text) => {
                if (isDemoMode) {
                  await new Promise((r) => setTimeout(r, 200));
                  return;
                }
                await getApiClient().createEmployeeNote(
                  selected.id,
                  { text },
                  crypto.randomUUID(),
                );
              }}
            />
          </div>
        ) : null}
      </section>
    </>
  );
}
